/**
 * Login Audit Page
 *
 * Displays login audit grid showing organizations and their login activity.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Clock, Eye } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { organizationService } from '@/api/services';
import type { LoginAuditOrganization, ListRequest } from '@/api/types';
import { formatDateTime } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';

// ============================================
// Page Component
// ============================================

export function LoginAuditPage() {
  const navigate = useNavigate();
  const toast = useToast();

  // State
  const [data, setData] = useState<LoginAuditOrganization[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch data
  const fetchAuditData = useCallback(
    async (params: { pageIndex: number; pageSize: number; sorting?: SortingState; search?: string }) => {
      setIsLoading(true);
      try {
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          sorting:
            params.sorting && params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : undefined,
          filtering: params.search ? { orgName: params.search } : undefined,
        };

        const result = await organizationService.getLoginAuditOrganizationGrid(request);
        setData(result.list);
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch audit data:', error);
        toast.error('Failed to load audit data');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial load
  useEffect(() => {
    fetchAuditData({ ...pagination, sorting, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearchTerm]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // View audit history
  const handleViewHistory = useCallback(
    (orgId: string) => {
      navigate(`/audit/${orgId}`);
    },
    [navigate]
  );

  // Column definitions
  const columns = useMemo<ColumnDef<LoginAuditOrganization>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Org Code',
        cell: ({ row }) => (
          <span className="font-medium text-foreground dark:text-foreground-dark">
            {row.original.orgCode}
          </span>
        ),
      },
      {
        accessorKey: 'orgName',
        header: 'Organization',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <span>{row.original.OrgName}</span>
          </div>
        ),
      },
      // {
      //   accessorKey: 'orgType',
      //   header: 'Type',
      //   cell: ({ row }) => (
      //     <Badge variant="outline">{row.original.orgType}</Badge>
      //   ),
      // },
      {
        accessorKey: 'orgStatus',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.OrgStatus;
          const variant =
            status === 'ACTIVE'
              ? 'success'
              : status === 'SUSPENDED'
                ? 'warning'
                : 'default';
          return (
            <Badge variant={variant} dot>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'loginUsersCount',
        header: 'Active Users',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            <span className="font-medium">{row.original.loginUsersCount}</span>
          </div>
        ),
      },
      {
        accessorKey: 'loginTime',
        header: 'Last Login',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Clock className="h-3 w-3" />
            {row.original.loginTime ? formatDateTime(row.original.loginTime) : 'N/A'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewHistory(row.original.orgId);
            }}
            leftIcon={<Eye className="h-4 w-4" />}
          >
            History
          </Button>
        ),
      },
    ],
    [handleViewHistory]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
          Login Audit
        </h1>
        <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
          Monitor login activity across all organizations
        </p>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="Organization Login Activity"
          description="Click 'History' to view detailed login history"
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search organizations..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage="No audit data found"
            emptyDescription="There is no login activity recorded yet."
            className="border-0"
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default LoginAuditPage;
